import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CARRIER_BID_VALIDITY_DAYS_DEFAULT,
  CARRIER_BID_VALIDITY_DAYS_MAX,
  CARRIER_BID_VALIDITY_DAYS_MIN,
} from '@magimp/shared';
import {
  Button,
  EditorModal,
  Input,
  Select,
  Textarea,
  useEditorGuard,
} from '../../../../ui/index.js';
import { errorMessage } from '../../../../api/errors.js';
import { useAddCarrierBid, usePatchCarrierBid } from '../../queries.js';
import type { BidUnit, CarrierBid, CarrierBidQuote } from '../../types.js';

export interface CarriersBidFormModalProps {
  requestId: string;
  bid: CarrierBid;
  quote: CarrierBidQuote | null;
  onClose: () => void;
}

// WHY (#1389): China bids pick validity from a guided preset list instead of
//   a raw day count. `manual` is the escape hatch (free integer input); the
//   other presets resolve to an integer `validityDays` client-side so the
//   wire contract (1..365, default 14) is unchanged.
export type ValidityPreset = 'thisWeek' | 'nextWeek' | '7' | '10' | '14' | 'manual';

// WHY (#1389): fixed-day presets that map 1:1 to an existing `validityDays`,
//   so an edit can re-select the matching preset instead of falling to manual.
const FIXED_VALIDITY_PRESETS: readonly number[] = [7, 10, 14];

/**
 * Resolve a non-manual validity preset to an integer day count.
 *
 * @aiPurpose Translate China bid validity presets into the wire `validityDays`.
 * @aiInvariant `thisWeek`/`nextWeek` use the Europe/Moscow calendar and never
 *   return 0 (backend min is 1): a Moscow Sunday resolves `thisWeek` to 1.
 */
export function presetValidityDays(
  preset: Exclude<ValidityPreset, 'manual'>,
  now: Date = new Date(),
): number {
  if (preset === '7' || preset === '10' || preset === '14') return Number(preset);
  // WHY: derive the Moscow weekday deterministically rather than trusting the
  //   runner's local zone, so the integer-day approximation is stable.
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Moscow',
    weekday: 'long',
  }).format(now);
  const dow = MOSCOW_WEEKDAY_INDEX[weekday] ?? 0;
  const daysToSunday = (7 - dow) % 7;
  const thisWeek = daysToSunday === 0 ? 1 : daysToSunday;
  return preset === 'nextWeek' ? thisWeek + 7 : thisWeek;
}

const MOSCOW_WEEKDAY_INDEX: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

// WHY (#1389): on edit, re-enter the matching fixed preset; dynamic week
//   presets are never auto-selected (they would change meaning by date).
function initialValidity(quote: CarrierBidQuote | null): {
  preset: ValidityPreset;
  manualDays: string;
} {
  if (quote == null) {
    return { preset: 'nextWeek', manualDays: String(CARRIER_BID_VALIDITY_DAYS_DEFAULT) };
  }
  if (FIXED_VALIDITY_PRESETS.includes(quote.validityDays)) {
    return { preset: String(quote.validityDays) as ValidityPreset, manualDays: '' };
  }
  return { preset: 'manual', manualDays: String(quote.validityDays) };
}

export function CarriersBidFormModal({
  requestId,
  bid,
  quote,
  onClose,
}: CarriersBidFormModalProps) {
  const { t } = useTranslation(['requests', 'common']);
  const addBid = useAddCarrierBid(requestId);
  const patchBid = usePatchCarrierBid(requestId);
  const [pricePerUnit, setPricePerUnit] = useState(quote ? String(quote.pricePerUnit) : '');
  const [currency, setCurrency] = useState<'USD' | 'RUB' | 'CNY'>(quote?.currency ?? 'USD');
  const [unit, setUnit] = useState<BidUnit>(quote?.unit ?? 'kg');
  // WHY (#1060): days are a min-max range on the wire. The form exposes
  //   two inputs; leaving both blank submits `null/null` (unspecified
  //   transit). Leaving only `max` blank collapses to `daysMin = daysMax`
  //   so single-day quotes are still one keystroke. The submit guard
  //   enforces `min <= max` when both are populated.
  const [daysMin, setDaysMin] = useState(quote?.daysMin != null ? String(quote.daysMin) : '');
  const [daysMax, setDaysMax] = useState(quote?.daysMax != null ? String(quote.daysMax) : '');
  const [comment, setComment] = useState(quote?.comment ?? '');
  const initialValidityState = useRef(initialValidity(quote)).current;
  const [validityPreset, setValidityPreset] = useState<ValidityPreset>(initialValidityState.preset);
  const [manualDays, setManualDays] = useState(initialValidityState.manualDays);
  // INVARIANT (#479): pin the dirty-diff baseline at first render via
  // refs. A background refetch must not move the baseline.
  const baselineRef = useRef({
    pricePerUnit,
    currency,
    unit,
    daysMin,
    daysMax,
    comment,
    validityPreset,
    manualDays,
  });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const normalizedPrice = Number(pricePerUnit.replace(',', '.'));
  const parsedDaysMin = daysMin.trim().length > 0 ? Number(daysMin) : null;
  const parsedDaysMax = daysMax.trim().length > 0 ? Number(daysMax) : null;
  const dayBoundValid = (v: number | null): boolean =>
    v === null || (Number.isInteger(v) && v > 0 && v < 10_000);
  // WHY (#1060): an unspecified max collapses to min (single-day bid).
  //   If both are set, `min <= max` is enforced. If only max is set
  //   (no min), the form rejects until min is filled.
  const daysMinValid = dayBoundValid(parsedDaysMin);
  const daysMaxValid = dayBoundValid(parsedDaysMax);
  const daysOrderValid =
    parsedDaysMin === null || parsedDaysMax === null || parsedDaysMin <= parsedDaysMax;
  const daysShapeValid = parsedDaysMin !== null || parsedDaysMax === null;
  const daysValid = daysMinValid && daysMaxValid && daysOrderValid && daysShapeValid;
  const isManualValidity = validityPreset === 'manual';
  // WHY (#1389): manual mode validates the raw integer (1..365) exactly as the
  //   old free input did; fixed/dynamic presets always resolve to a valid day.
  const resolvedValidity = isManualValidity
    ? Number(manualDays)
    : presetValidityDays(validityPreset);
  const validityValid =
    Number.isInteger(resolvedValidity) &&
    resolvedValidity >= CARRIER_BID_VALIDITY_DAYS_MIN &&
    resolvedValidity <= CARRIER_BID_VALIDITY_DAYS_MAX;
  const canSubmit =
    Number.isFinite(normalizedPrice) && normalizedPrice >= 0 && daysValid && validityValid;

  async function onSubmit(): Promise<void> {
    if (!canSubmit) return;
    setSubmitError(null);
    const submitDaysMin = parsedDaysMin;
    // WHY (#1060): empty max but populated min ⇒ single-day bid; collapse
    //   `max` to `min` so the wire keeps `daysMin <= daysMax`.
    const submitDaysMax = parsedDaysMax ?? parsedDaysMin;
    // WHY (#1389): recompute the validity at submit time (fresh `new Date()`)
    //   so a `thisWeek`/`nextWeek` bid kept open across the Europe/Moscow
    //   midnight boundary submits today's day count, not the stale render-time
    //   one. `resolvedValidity` stays render-time for gating only.
    const submitValidity = isManualValidity
      ? Number(manualDays)
      : presetValidityDays(validityPreset);
    const body = {
      pricePerUnit: normalizedPrice,
      currency,
      unit,
      daysMin: submitDaysMin,
      daysMax: submitDaysMax,
      comment: comment.trim(),
      validityDays: submitValidity,
    };
    try {
      if (quote) {
        await patchBid.mutateAsync({ requestCarrierId: bid.id, bidId: quote.id, body });
      } else {
        await addBid.mutateAsync({ requestCarrierId: bid.id, body });
      }
      baselineRef.current = {
        pricePerUnit: String(body.pricePerUnit),
        currency: body.currency,
        unit: body.unit,
        daysMin: body.daysMin != null ? String(body.daysMin) : '',
        daysMax: body.daysMax != null ? String(body.daysMax) : '',
        comment: body.comment,
        validityPreset,
        manualDays,
      };
      onClose();
    } catch (err) {
      setSubmitError(errorMessage(err));
    }
  }

  const submitting = quote ? patchBid.isPending : addBid.isPending;
  const baseline = baselineRef.current;
  const dirty =
    pricePerUnit.trim() !== baseline.pricePerUnit.trim() ||
    currency !== baseline.currency ||
    unit !== baseline.unit ||
    daysMin.trim() !== baseline.daysMin.trim() ||
    daysMax.trim() !== baseline.daysMax.trim() ||
    comment.trim() !== baseline.comment.trim() ||
    validityPreset !== baseline.validityPreset ||
    manualDays.trim() !== baseline.manualDays.trim();

  return (
    <EditorModal
      open
      dirty={dirty}
      busy={submitting}
      onConfirmClose={onClose}
      title={
        quote
          ? t('requests:carriers.editBidTitle', { name: bid.carrier.name })
          : t('requests:carriers.addBidTitle', { name: bid.carrier.name })
      }
      width={480}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px', gap: 10 }}>
        <Input
          value={pricePerUnit}
          onChange={setPricePerUnit}
          placeholder={t('requests:carriers.pricePerUnitPlaceholder')}
          invalid={
            pricePerUnit.length > 0 && !(Number.isFinite(normalizedPrice) && normalizedPrice >= 0)
          }
        />
        <Select
          value={currency}
          options={[
            { value: 'USD', label: 'USD' },
            { value: 'RUB', label: 'RUB' },
            { value: 'CNY', label: 'CNY' },
          ]}
          onChange={(v) => setCurrency(v as 'USD' | 'RUB' | 'CNY')}
        />
        <Select
          value={unit}
          options={[
            { value: 'kg', label: t('requests:carriers.unitKg') },
            { value: 'm3', label: t('requests:carriers.unitM3') },
            { value: 'piece', label: t('requests:carriers.unitPiece') },
          ]}
          onChange={(v) => setUnit(v as BidUnit)}
        />
      </div>
      <div style={{ height: 10 }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Input
          value={daysMin}
          onChange={setDaysMin}
          placeholder={t('requests:carriers.daysMinPlaceholder')}
          invalid={!daysMinValid || !daysShapeValid}
        />
        <Input
          value={daysMax}
          onChange={setDaysMax}
          placeholder={t('requests:carriers.daysMaxPlaceholder')}
          invalid={!daysMaxValid || !daysOrderValid}
        />
      </div>
      <div style={{ height: 10 }} />
      <div>
        <Select
          value={validityPreset}
          options={[
            { value: 'thisWeek', label: t('requests:carriers.bid.validityPreset.thisWeek') },
            { value: 'nextWeek', label: t('requests:carriers.bid.validityPreset.nextWeek') },
            { value: '7', label: t('requests:carriers.bid.validityPreset.days7') },
            { value: '10', label: t('requests:carriers.bid.validityPreset.days10') },
            { value: '14', label: t('requests:carriers.bid.validityPreset.days14') },
            { value: 'manual', label: t('requests:carriers.bid.validityPreset.manual') },
          ]}
          onChange={(v) => setValidityPreset(v as ValidityPreset)}
          style={{ width: '100%' }}
        />
        {isManualValidity && (
          <>
            <div style={{ height: 10 }} />
            <Input
              value={manualDays}
              onChange={setManualDays}
              placeholder={t('requests:carriers.bid.validityDaysPlaceholder')}
              invalid={!validityValid}
            />
            <div style={{ fontSize: 11, color: 'var(--fg-3)', marginTop: 4 }}>
              {t('requests:carriers.bid.validityHint', {
                min: CARRIER_BID_VALIDITY_DAYS_MIN,
                max: CARRIER_BID_VALIDITY_DAYS_MAX,
                defaultDays: CARRIER_BID_VALIDITY_DAYS_DEFAULT,
              })}
            </div>
          </>
        )}
      </div>
      <div style={{ height: 10 }} />
      <Textarea
        value={comment}
        onChange={setComment}
        placeholder={t('requests:carriers.commentPlaceholder')}
        rows={4}
      />
      {submitError && (
        <div style={{ marginTop: 10, color: 'var(--status-error-fg)', fontSize: 12 }}>
          {submitError}
        </div>
      )}
      <CarriersModalFooter>
        <CarriersModalCancel onClose={onClose} label={t('common:actions.cancel')} />
        <Button
          kind="primary"
          disabled={!canSubmit || submitting}
          onClick={() => {
            void onSubmit();
          }}
        >
          {t('common:actions.save')}
        </Button>
      </CarriersModalFooter>
    </EditorModal>
  );
}

// WHY: Cancel must call `useEditorGuard().confirmDestructive(onClose)` to
// prompt on dirty close. The EditorGuardContext provider only wraps the
// EditorModal `children` subtree, not the `footer` prop, so action buttons
// render inline at the bottom of children instead. The footer div mirrors
// the visual style of the Modal footer slot.
export function CarriersModalFooter({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        marginTop: 12,
        marginLeft: -16,
        marginRight: -16,
        marginBottom: -16,
        padding: '12px 16px',
        borderTop: '1px solid var(--border-2)',
        display: 'flex',
        gap: 8,
        justifyContent: 'flex-end',
      }}
    >
      {children}
    </div>
  );
}

export function CarriersModalCancel({ onClose, label }: { onClose: () => void; label: string }) {
  const { confirmDestructive } = useEditorGuard();
  return (
    <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
      {label}
    </Button>
  );
}
